import { Button, Stack, TextField, Typography } from "@mui/material";
import { GoogleLogin } from "@react-oauth/google"; // Import Google Login
import { useQueryClient } from "@tanstack/react-query";
import { HttpStatusCode } from "axios";
import React, { Fragment } from "react";
import { useTranslation } from "react-i18next";
import { Link, useNavigate, useParams } from "react-router-dom";
import LanguageSwitcher from "../../../../components/LanguageSwitcher";
import { SnackbarContext } from "../../../../contexts/Snackbar";
import { LOGGED_IN_USER, LOGIN_KEYS } from "../../../../enums/QueryKeys";
import * as UrlPath from "../../../../enums/UrlPaths";
import { parseLoginData } from "../../../../hooks/useUser";
import authApi from "../../../../utils/api/authApi";
import * as Functions from "../../../../utils/Functions";
import { ThemeContext } from "../../../../contexts/ThemeContext";
import { CALENDAR_VIEW } from "../../../../enums/LocalStorageValues";

export default function Buttons() {
  let { shortName } = useParams();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { i18n, t } = useTranslation();
  const { setTheme } = React.useContext(ThemeContext);
  const { handleSnackbar } = React.useContext(SnackbarContext);

  // Set while the backend is waiting for the account password before it will attach this
  // Google identity to an existing password account. Holds the Google token so the user
  // doesn't have to re-run the Google flow after typing their password.
  const [pendingLinkToken, setPendingLinkToken] = React.useState(null);
  const [linkPassword, setLinkPassword] = React.useState("");
  const [linkBusy, setLinkBusy] = React.useState(false);

  const completeLogin = async (res) => {
    queryClient.setQueryData([LOGGED_IN_USER], res?.data);
    queryClient.setQueryData([LOGIN_KEYS], parseLoginData(res?.data));

    const loginKeys = await queryClient.getQueryData([LOGIN_KEYS]);
    Functions.setLocalStorageData(res.data);
    setDefaultUserPreferences(res?.data);

    const lastUrl = Functions.getLastVisitedUrl();
    if (lastUrl) {
      Functions.removeLastUrl();
      navigate(lastUrl);
    } else {
      if (Boolean(loginKeys?.needToAddPhoneNumber)) {
        navigate(UrlPath.PROFILE_EDIT, {
          state: { missingPhoneNumber: true },
        });
      } else {
        navigate(UrlPath.HOME);
      }
    }
  };

  const submitGoogleToken = async (token, password) => {
    try {
      setLinkBusy(true);
      const res = await authApi.post(
        "/auth/login/google",
        password ? { token, password } : { token }
      );

      if (res.status === HttpStatusCode.Ok) {
        setPendingLinkToken(null);
        setLinkPassword("");
        await completeLogin(res);
      }
    } catch (error) {
      const status = error?.response?.status;
      const data = error?.response?.data;

      // The email matches an existing password account. Keep the Google token and ask for
      // the password so the user can prove the account is theirs before it gets linked.
      if (
        status === HttpStatusCode.Conflict &&
        data === "PasswordRequiredToLinkAccount"
      ) {
        setPendingLinkToken(token);
        setLinkPassword("");
        handleSnackbar(data, "info");
        return;
      }

      // Wrong password mid-link: keep the prompt open so they can retry.
      if (status === HttpStatusCode.Unauthorized && pendingLinkToken) {
        handleSnackbar(data, "error");
        return;
      }

      setPendingLinkToken(null);
      setLinkPassword("");
      handleSnackbar(data, "error");
    } finally {
      setLinkBusy(false);
    }
  };

  // Google OAuth handler
  const handleGoogleLoginSuccess = async (response) => {
    // Block clinics from attempting to register using google account.
    // Must come BEFORE the request: this used to run after it, so the account was
    // already created/linked server-side and only the client-side result was discarded.
    // Unreachable today (the button below only renders when shortName is absent), but
    // it has to be correct if that ever changes.
    if (shortName) {
      handleSnackbar("GoogleSignUpBlockedForClinicRegistration", "error");
      return null;
    }

    await submitGoogleToken(response.credential);
  };

  const handleLinkSubmit = async (event) => {
    event.preventDefault();
    if (!linkPassword) return;
    await submitGoogleToken(pendingLinkToken, linkPassword);
  };

  const cancelLink = () => {
    setPendingLinkToken(null);
    setLinkPassword("");
  };

  function setDefaultUserPreferences(data) {
    const defaultLanguage = data?.defaultLanguage
      ? data?.defaultLanguage
      : i18n.language;
    setTheme(data?.defaultTheme ?? "dark");
    i18n.changeLanguage(defaultLanguage);
    localStorage.setItem("lang", defaultLanguage);
    localStorage.setItem(CALENDAR_VIEW, data?.defaultCalendarView ?? "agenda");
  }

  // Google OAuth failure handler
  const handleGoogleLoginFailure = (error) => {
    handleSnackbar(error?.response?.data, "error");
  };
  return (
    <Stack
      width={"100%"}
      spacing={2}
      display={"flex"}
      justifyContent={"center"}
      alignItems={"center"}
    >
      <Fragment>
        {shortName ? (
          <Button
            size="large"
            variant="outlined"
            onClick={() => navigate(UrlPath.LOGIN)}
            data-testid="auth.login.switch-to-user-login-button"
          >
            {t("UserLogin")}
          </Button>
        ) : (
          <Stack spacing={2} justifyContent={"center"} alignItems={"center"}>
            <GoogleLogin
              onSuccess={handleGoogleLoginSuccess}
              onError={handleGoogleLoginFailure}
            />

            {pendingLinkToken && (
              <Stack
                component="form"
                onSubmit={handleLinkSubmit}
                spacing={1}
                width={"100%"}
                alignItems={"center"}
                data-testid="auth.login.google-link-prompt"
              >
                <Typography variant="caption" textAlign={"center"}>
                  {t("PasswordRequiredToLinkAccount")}
                </Typography>
                <TextField
                  type="password"
                  size="small"
                  fullWidth
                  autoFocus
                  autoComplete="current-password"
                  label={t("Password")}
                  value={linkPassword}
                  onChange={(e) => setLinkPassword(e.target.value)}
                  inputProps={{ "data-testid": "auth.login.link-password-input" }}
                />
                <Stack direction="row" spacing={1}>
                  <Button
                    type="submit"
                    variant="contained"
                    disabled={linkBusy || !linkPassword}
                    data-testid="auth.login.link-submit-button"
                  >
                    {t("LinkAndSignIn")}
                  </Button>
                  <Button
                    variant="outlined"
                    onClick={cancelLink}
                    disabled={linkBusy}
                    data-testid="auth.login.link-cancel-button"
                  >
                    {t("Cancel")}
                  </Button>
                </Stack>
              </Stack>
            )}

            <Stack alignItems={"center"}>
              <Typography variant="caption" textAlign={"center"}>
                {t("LoginTermsAndServicesAgreement")}
              </Typography>
              <Typography variant="caption">
                <Link to={UrlPath.TERMS} data-testid="auth.login.terms-link">
                  {t("LoginTerms")}
                </Link>
              </Typography>
            </Stack>
          </Stack>
        )}
      </Fragment>
      <LanguageSwitcher />
    </Stack>
  );
}
